import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "~/components/ui/dialog";

import { useQuery } from "@tanstack/react-query";
import { useStore } from "@tanstack/react-store";
import { useQueryParams } from "~/hooks/useQueryParams";

import { ArrowLeftIcon } from "@/components/icons";
import { useMobile } from "@/hooks/useMedia";
import { useTheme } from "@/hooks/useTheme";
import { authStore } from "@/lib/auth/store";
import { modalStore } from "@/lib/stores/modals";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { useAccountModal, useConnectModal } from "@rainbow-me/rainbowkit";
// import { Elements } from "@stripe/react-stripe-js";
// import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAccount, useDisconnect } from "wagmi";
import { useLanguage } from "~/hooks/useLanguage";
import { MemberLevel } from "~/routes/$lang/profile/-api";
import CryptoPay from "./CryptoPay";
import GemPackCard from "./GemPackCard";
import MemberPackCard from "./MemberPackCard";
import PaymentMethodSelector from "./PaymentMethodSelector";
import PaymentNotice from "./PaymentNotice";
import {
	type OrderProduct,
	getOrderProducts,
	getSubscriptionInfo,
} from "./api";

export const memberOrder = [
	MemberLevel.STANDARD,
	MemberLevel.PREMIUM,
	MemberLevel.SUPREME,
];

// console.log(memberOrder, "-----memberOrder----");

export default function DepositModal() {
	const isMobile = useMobile();
	const theme = useTheme();
	const search = useQueryParams();
	const hasShownToast = useRef(false);
	const user = useStore(authStore, (s) => s.user);

	const { depositModalOpen, isRainbowModalOpen } = useStore(modalStore);
	const lang = useLanguage();

	const MODAL_WIDTH = {
		DEFAULT: lang === "zh" ? 850 : 850,
		PAYMENT: 590,
	};
	const [modalWidth, setModalWidth] = useState<string>(
		`${MODAL_WIDTH.DEFAULT}px`,
	);
	const [isShowBack, setIsShowBack] = useState(false);

	const [selectedProduct, setSelectedProduct] = useState<OrderProduct | null>(
		null,
	);

	// const [stripeOpen, setStripeOpen] = useState(false);
	const [cryptoOpen, setCryptoOpen] = useState(false);
	const [pendingWalletChange, setPendingWalletChange] = useState(false);

	const prevAddress = useRef<string | undefined>(undefined);
	const { address, isConnected } = useAccount();
	const { disconnect } = useDisconnect();

	const { accountModalOpen: isAccountModalOpen } = useAccountModal();
	const { openConnectModal, connectModalOpen: isConnectModalOpen } =
		useConnectModal();

	const handleChangeWallet = useCallback(async () => {
		setPendingWalletChange(true);
		if (disconnect) {
			await disconnect();
		}
		openConnectModal?.();
	}, [openConnectModal, disconnect]);

	useEffect(() => {
		if (pendingWalletChange && address && address !== prevAddress.current) {
			setCryptoOpen(true);
			setPendingWalletChange(false);
		}
		prevAddress.current = address;

		modalStore.setRainbowModalOpen(isConnectModalOpen || isAccountModalOpen);

		if (isMobile) {
			setModalWidth("100%");
		} else {
			setModalWidth(
				`${cryptoOpen ? MODAL_WIDTH.PAYMENT : MODAL_WIDTH.DEFAULT}px`,
			);
		}
		setIsShowBack(cryptoOpen);
	}, [
		address,
		pendingWalletChange,
		isConnectModalOpen,
		isAccountModalOpen,
		cryptoOpen,
		isMobile,
	]);

	useEffect(() => {
		if (!isConnected && cryptoOpen) {
			setCryptoOpen(false);
		}
	}, [isConnected, cryptoOpen]);

	useEffect(() => {
		if (pendingWalletChange && !isConnected) {
			openConnectModal?.();
		}

		// 只有在明确需要跳转到 crypto 支付时才自动打开
		if (
			isConnected &&
			!pendingWalletChange &&
			sessionStorage.getItem("crypto_back_to_payment") === "1"
		) {
			setPendingWalletChange(false);
			setCryptoOpen(true);
			sessionStorage.removeItem("crypto_back_to_payment");
		}
	}, [pendingWalletChange, isConnected, openConnectModal, cryptoOpen]);

	const handleDialogOpenChange = useCallback(
		(open: boolean) => {
			if (!open && isRainbowModalOpen) return;
			modalStore.setDepositModalOpen(open);
			if (!open) {
				// 清除 URL 中的 deposit 相关参数
				const url = new URL(window.location.href);
				url.searchParams.delete("deposit_success");
				url.searchParams.delete("deposit_fullback");
				window.history.replaceState({}, "", url.toString());

				setTimeout(() => {
					setIsShowBack(false);
					setModalWidth(`${MODAL_WIDTH.DEFAULT}px`);
					setCryptoOpen(false);
					setSelectedProduct(null);
					hasShownToast.current = false;
				}, 300);
			}
		},
		[isRainbowModalOpen],
	);

	const [orderProducts, setOrderProducts] = useState(() => {
		const cache = sessionStorage.getItem("orderProducts");
		sessionStorage.removeItem("orderProducts");
		return cache ? JSON.parse(cache) : null;
	});

	useEffect(() => {
		if (!orderProducts && user) {
			getOrderProducts().then((data) => {
				setOrderProducts(data);
				// sessionStorage.setItem("orderProducts", JSON.stringify(data));
			});
		}
	}, [orderProducts, user]);

	const { data: subscriptionInfo } = useQuery({
		queryKey: ["subscriptionInfo"],
		queryFn: getSubscriptionInfo,
		enabled: !!user,
	});

	useEffect(() => {
		if (
			search &&
			typeof search === "object" &&
			search.deposit_fullback &&
			!hasShownToast.current
		) {
			handleDialogOpenChange(true);
			if (search.deposit_success === "true") {
				toast.success(m.deposit_success());
			}
			hasShownToast.current = true;
		}
	}, [search]);

	const gemPacks = useMemo(() => {
		return orderProducts?.data?.products?.filter(
			(item: OrderProduct) => item.type === "Once",
		);
	}, [orderProducts]);

	const memberPacks = useMemo(() => {
		return orderProducts?.data?.products
			?.filter((item: OrderProduct) => item.type === "Subscription")
			?.sort(
				(a: OrderProduct, b: OrderProduct) =>
					memberOrder.indexOf(a.name) - memberOrder.indexOf(b.name),
			);
	}, [orderProducts]);

	useEffect(() => {
		if (sessionStorage.getItem("deposit_auto_open") === "1") {
			modalStore.setDepositModalOpen(true);
			sessionStorage.removeItem("deposit_auto_open");
		}
	}, []);

	return (
		<div>
			<Dialog open={depositModalOpen} onOpenChange={handleDialogOpenChange}>
				<DialogContent
					className="w-[calc(100vw-32px)] md:w-auto max-h-[654px]"
					style={{ width: modalWidth, maxWidth: modalWidth }}
				>
					<DialogHeader className="h-[30px] md:h-auto">
						<DialogTitle>
							<div
								className="flex items-center gap-2 cursor-pointer hover:opacity-80"
								onClick={() => {
									setIsShowBack(false);
									setModalWidth(`${MODAL_WIDTH.DEFAULT}px`);
									setCryptoOpen(false);
									setPendingWalletChange(false);
									sessionStorage.setItem("crypto_back_to_payment", "0");
								}}
							>
								{isShowBack && <ArrowLeftIcon className="[&>path]:stroke-t1" />}
								<span>{m.deposit()}</span>
							</div>
						</DialogTitle>
					</DialogHeader>
					<div className="max-h-[580px] overflow-y-auto pr-4 md:pr-6">
						<div className="flex justify-center">
							<div
								className={cn("relative w-full", {
									hidden: cryptoOpen,
								})}
							>
								<div className="mb-4">
									<div className="grid grid-cols-2 gap-[9px] md:grid-cols-4">
										{gemPacks?.map((item: OrderProduct) => (
											<GemPackCard
												key={item.price}
												item={item}
												selected={selectedProduct?.id === item.id}
												onClick={() => {
													setSelectedProduct(item);
												}}
											/>
										))}
									</div>
								</div>
								<div className="mb-4">
									<div className="flex gap-[9px] flex-col md:flex-row">
										{memberPacks?.map((item: OrderProduct) => (
											<MemberPackCard
												key={item.price}
												item={item}
												selected={selectedProduct?.id === item.id}
												theme={theme}
												onClick={() => setSelectedProduct(item)}
												subscriptionInfo={
													subscriptionInfo?.data?.subscription_info
												}
											/>
										))}
									</div>
								</div>
								<PaymentMethodSelector
									orderProducts={orderProducts}
									supportedChannels={orderProducts?.data?.supported_channels}
									selectedProduct={selectedProduct}
									setCryptoOpen={setCryptoOpen}
									isConnected={isConnected}
									openConnectModal={openConnectModal}
									setPendingWalletChange={setPendingWalletChange}
								/>
								<PaymentNotice />
							</div>
							{cryptoOpen && isConnected && selectedProduct && (
								<CryptoPay
									open={cryptoOpen}
									product={selectedProduct}
									onOpenChange={() => setCryptoOpen(false)}
									onChangeWallet={handleChangeWallet}
								/>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
